
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { NavItem } from '../types';
import { NAVIGATION_ITEMS, AppLogoIcon, ChevronRightIcon, LogoutIcon } from '../constants';
import LoginModal from './LoginModal';

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  activeItemId: string;
  onNavItemClick: (itemId: string) => void;
  onAuthChange?: (isAuthenticated: boolean) => void;
  requireLogin?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggleCollapse, activeItemId, onNavItemClick, onAuthChange, requireLogin = false }) => {
  const [currentUser, setCurrentUser] = useState<{ 
    id: string; 
    username: string; 
    category: string;
    login_timeout_minutes?: number | null;
    login_time?: string;
    is_default?: boolean;
  } | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [modalRequireLogin, setModalRequireLogin] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [shouldLogout, setShouldLogout] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('currentUser');
    const wasManualLogout = sessionStorage.getItem('manualLogout') === 'true';
    console.log('Sidebar loading, stored user:', storedUser, 'manual logout:', wasManualLogout);
    
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      console.log('Parsed user data:', parsedUser);
      setCurrentUser(parsedUser);
      onAuthChange?.(true);
    } else if (!wasManualLogout) {
      // Only check for default user if it wasn't a manual logout
      checkForDefaultUser();
    } else {
      // Manual logout - check if we need to require login
      checkDefaultAndShowModal();
    }
  }, []);
  
  const checkForDefaultUser = async () => {
    try {
      const response = await fetch('/api/users/default');
      const data = await response.json();
      
      if (data.message === 'success' && data.data) {
        console.log('Default user found, auto-logging in:', data.data);
        // Auto-login as default user
        localStorage.setItem('currentUser', JSON.stringify(data.data));
        setCurrentUser(data.data);
        setShowLoginModal(false);
        onAuthChange?.(true);
      } else {
        console.log('No default user found, showing login modal with require login');
        // No default user, show login modal that can't be closed
        setModalRequireLogin(true);
        setShowLoginModal(true);
        onAuthChange?.(false);
      }
    } catch (error) {
      console.error('Error checking for default user:', error);
      setModalRequireLogin(true);
      setShowLoginModal(true);
      onAuthChange?.(false);
    }
  };
  
  const checkDefaultAndShowModal = async () => {
    try {
      const response = await fetch('/api/users/default');
      const data = await response.json();
      
      if (data.message === 'success' && data.data) {
        console.log('Default user found, showing modal with cancel option');
        // Default user exists, show modal with cancel button
        setModalRequireLogin(false);
        setShowLoginModal(true);
      } else {
        console.log('No default user found, showing modal without cancel option');
        // No default user, require login
        setModalRequireLogin(true);
        setShowLoginModal(true);
        onAuthChange?.(false);
      }
    } catch (error) {
      console.error('Error checking for default user:', error);
      setModalRequireLogin(true);
      setShowLoginModal(true);
      onAuthChange?.(false);
    }
  };

  // Timer effect for login timeout
  useEffect(() => {
    console.log('Timer effect running, currentUser:', currentUser);
    
    // Only start timer if user has timeout AND login_time
    if (!currentUser) {
      console.log('No current user, skipping timer');
      setTimeRemaining(null);
      return;
    }
    
    if (typeof currentUser.login_timeout_minutes !== 'number') {
      console.log('No timeout set or not a number:', currentUser.login_timeout_minutes);
      setTimeRemaining(null);
      return;
    }
    
    if (!currentUser.login_time) {
      console.log('No login_time set:', currentUser.login_time);
      setTimeRemaining(null);
      return;
    }

    console.log('Starting timer with:', {
      timeout_minutes: currentUser.login_timeout_minutes,
      login_time: currentUser.login_time
    });

    const updateTimer = () => {
      const loginTime = new Date(currentUser.login_time).getTime();
      const timeoutMs = currentUser.login_timeout_minutes * 60 * 1000;
      const now = Date.now();
      const elapsed = now - loginTime;
      const remaining = timeoutMs - elapsed;

      console.log('Timer update:', {
        loginTime: new Date(loginTime).toISOString(),
        now: new Date(now).toISOString(),
        elapsed: Math.floor(elapsed / 1000) + 's',
        remaining: Math.floor(remaining / 1000) + 's',
        timeoutMs: timeoutMs + 'ms'
      });

      if (remaining <= 0) {
        console.log('Timer expired! Triggering logout');
        // Timeout expired - trigger logout
        setShouldLogout(true);
        setTimeRemaining(0);
      } else {
        // Update remaining time in seconds
        const secondsRemaining = Math.floor(remaining / 1000);
        console.log('Setting time remaining:', secondsRemaining);
        setTimeRemaining(secondsRemaining);
      }
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => {
      console.log('Cleaning up timer interval');
      clearInterval(interval);
    };
  }, [currentUser]);
  
  // Handle logout when timer expires
  useEffect(() => {
    if (shouldLogout) {
      setShouldLogout(false);
      handleTimerExpired();
    }
  }, [shouldLogout]);

  const handleLogin = (user: { 
    id: string; 
    username: string; 
    category: string;
    login_timeout_minutes?: number | null;
    login_time?: string;
    is_default?: boolean;
  }) => {
    console.log('handleLogin called with user:', user);
    sessionStorage.removeItem('manualLogout'); // Clear manual logout flag
    setCurrentUser(user);
    setShowLoginModal(false);
    onAuthChange?.(true);
  };

  const handleLogout = async () => {
    console.log('Logout button clicked - checking for default user and showing login modal');
    // Clear current user
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    setTimeRemaining(null);
    
    // Check if default user exists to determine modal behavior
    try {
      const response = await fetch('/api/users/default');
      const data = await response.json();
      
      if (data.message === 'success' && data.data) {
        console.log('Default user exists, showing modal with cancel option');
        // Default user exists, show modal with cancel button
        setModalRequireLogin(false);
        // Set default user as preview but DON'T store in localStorage yet
        setCurrentUser(data.data);
      } else {
        console.log('No default user, showing modal without cancel option');
        // No default user, require login
        setModalRequireLogin(true);
      }
    } catch (error) {
      console.error('Error checking for default user:', error);
      setModalRequireLogin(true);
    }
    
    // Show login modal
    setShowLoginModal(true);
  };
  
  const handleTimerExpired = async () => {
    console.log('Timer expired - reverting to default user');
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    setTimeRemaining(null);
    // Don't set manual logout flag - we want to auto-login as default
    checkForDefaultUser();
  };
  
  const handleResetSession = () => {
    if (currentUser && currentUser.login_timeout_minutes) {
      // Reset the session by updating the login time to now
      const updatedUser = {
        ...currentUser,
        login_time: new Date().toISOString()
      };
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);
      console.log('Session timer reset');
    }
  };

  const formatTimeRemaining = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };
  return (
    <div className={`bg-slate-50 text-slate-700 flex flex-col transition-all duration-300 ease-in-out h-full shadow-lg relative ${isCollapsed ? 'w-16' : 'w-64'}`}>

      {/* Logo and version */}
      <div className={`flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-4'} py-4 h-20 border-b border-slate-200`}>
        <img 
          src="/DCH-logo.png" 
          alt="DCH Logo" 
          className={`${isCollapsed ? 'w-12 h-12' : 'w-10 h-10'} object-contain`}
        />
        {!isCollapsed && (
          <div className="ml-3">
            <h1 className="text-xl font-bold text-slate-800">Yield Track</h1>
            <span className="text-xs text-slate-500">v 1.20</span>
          </div>
        )}
      </div>

      {/* Navigation Items */}
      <nav className="flex-grow mt-4 space-y-1 px-2">
        {NAVIGATION_ITEMS.map((item: NavItem) => (
          <Link
            to={item.path}
            key={item.id}
            onClick={(e) => {
              // If clicking on the already selected item, toggle collapse
              if (activeItemId === item.id) {
                e.preventDefault(); // Prevent navigation
                onToggleCollapse();
              } else {
                onNavItemClick(item.id);
              }
            }}
            className={`flex items-center py-3 px-3 rounded-lg transition-colors duration-150 ease-in-out group
              ${activeItemId === item.id ? 'bg-indigo-600 text-white shadow-md cursor-pointer' : 'hover:bg-indigo-100 hover:text-indigo-700 text-slate-600'}
              ${isCollapsed ? 'justify-center' : ''}`}
            title={isCollapsed ? item.label : (activeItemId === item.id ? 'Click to ' + (isCollapsed ? 'expand' : 'collapse') + ' menu' : undefined)}
          >
            <span className={`${activeItemId === item.id ? 'text-white' : 'text-slate-500 group-hover:text-indigo-600'}`}>
              {item.icon}
            </span>
            {!isCollapsed && <span className="ml-3 font-medium">{item.label}</span>}
            {!isCollapsed && activeItemId === item.id && (
              <svg className="ml-auto w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M8 9l4-4 4 4m0 6l-4 4-4-4"
                />
              </svg>
            )}
            {!isCollapsed && activeItemId !== item.id && (
              <ChevronRightIcon className={`ml-auto w-4 h-4 transition-opacity duration-200 opacity-0 group-hover:opacity-100`} />
            )}
          </Link>
        ))}
      </nav>

      {/* User Info and Logout */}
      <div className="p-3 border-t border-slate-200 mt-auto">
        {currentUser ? (
          !isCollapsed ? (
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{currentUser.username}</p>
                  <p className="text-xs text-slate-500 capitalize">{currentUser.category}</p>
                </div>
                <button 
                  onClick={handleLogout}
                  className="p-2 rounded-md text-teal-600 bg-teal-100 hover:bg-teal-200 transition-colors duration-150"
                  title="Logout"
                >
                  <LogoutIcon className="w-5 h-5" />
                </button>
              </div>
              {timeRemaining !== null && !currentUser?.is_default && (
                <div 
                  className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md cursor-pointer hover:bg-amber-100 transition-colors"
                  onClick={handleResetSession}
                  title="Click to reset session timer"
                >
                  <p className="text-xs font-medium text-amber-800">
                    Session expires in: 
                    <span className={`ml-1 font-bold ${timeRemaining < 60 ? 'text-red-600' : ''}`}>
                      {formatTimeRemaining(timeRemaining)}
                    </span>
                  </p>
                </div>
              )}
            </div>
          ) : (
            <button 
              onClick={handleLogout}
              className="w-full p-2 rounded-md text-teal-600 bg-teal-100 hover:bg-teal-200 flex justify-center transition-colors duration-150"
              title="Logout"  
            >
              <LogoutIcon className="w-5 h-5" />
            </button>
          )
        ) : (
          <button
            onClick={() => {
          checkDefaultAndShowModal();
        }}
            className={`${isCollapsed ? 'w-full justify-center' : ''} px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2`}
          >
            {!isCollapsed && <span>Login</span>}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
          </button>
        )}
      </div>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          // If login is required (no default user), don't allow closing the modal
          if (modalRequireLogin && !currentUser) {
            console.log('Login required - cannot close modal without logging in');
            return;
          }
          
          setShowLoginModal(false);
          // When modal is closed (cancelled), check for default user
          checkForDefaultUser();
        }}
        onLogin={handleLogin}
        requireLogin={modalRequireLogin}
      />
    </div>
  );
};

export default Sidebar;
